import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useGettingStartedForm } from './hooks/useGettingStartedForm';
import { FormProvider, useWatch } from 'react-hook-form';
import { FormInput } from '~/components/FormComponents/FormInput';
import { CreationClientInfo } from './components/CreationClientInfo';
import { useAppDispatch } from '~/store/appDispatch';
import {
  setGettingStarted,
  setIdentificationError,
  setIsReidentification,
  updateFormData,
  updateFormDataBack,
} from '~/store/identification-form/slices';
import { fetchApplicationStatus, fetchB2CheckResult, getContragentType } from './utils';
import { contragentTypes, getManagerNotFoundMessage, QUESTION_FIELDS } from './constants.tsx';
import api from '~/libs/api.ts';
import {
  IdentificationType,
  IContinueProps,
  InfoModalButtonTitle,
  InfoStatusContragentCodes,
  TApplicationStatus,
  TB2CheckResult,
  TGettingStarted,
} from './types';
import VerificationBlock from './components/VerificationBlock';
import { infoMessages } from '~/constants/infoMessages.ts';
import { InfoTooltip } from '~/components/InfoTooltip/InfoTooltip';
import {
  fetchCreatedIdentificationForm,
  fetchIdentificationForm,
  managerCreateApplication,
  updateForm,
  updateFormChangeVideoVerification,
} from '~/store/identification-form/thunks/thunks';
import { TIdentificationForm } from '~/store/identification-form/types';
import { errorMessages } from '~/constants/errorMessages.ts';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { cloneDeep } from 'lodash';
import FullPageLoader from '~/components/FullPageLoader/FullPageLoader';
import { CLIENT_TYPES_ID, ClientTypes } from '~/constants/clientTypes.ts';
import { VerificationChoice } from '~/components/VerificationModal/types';
import useGetUserRole from '../../../../hooks/useGetUserRole.ts';
import { ERROR_CODES } from '~/constants/errorCodes.ts';
import { CreationClientInfoReIdentification } from '~/modules/identification/forms/GettingStarted/components/CreationClientInfoReIdentification.tsx';
import { IdentificationFormStepNames, IdentificationFormSteps } from '../../types.ts';
import { styles } from '~/modules/identification/forms/GettingStarted/styles.ts';
import { AxiosError } from 'axios';
import { TErrorResponse } from '~/store/types.ts';
import { useSetSearchParam } from '~/hooks/useSetSearchParam.ts';
import { commonStyles } from '~/theme/styles/commonStyles.ts';
import { onInvalidForm } from '~/helpers/utils.ts';
import { useIdentificationFormContext } from '../../contexts/IdentificationFormContext.ts';
import { SetDirtyModalValues } from '../../components/SetDirtyModalValues.tsx';
import {
  selectCompletedForms,
  selectGettingStartedBack,
  selectIdentification,
} from '~/store/identification-form/selectors.ts';
import { selectDiia } from '~/store/diia/selectors.ts';

interface IGettingStartedProps {
  onNextStep: () => void;
}

const GettingStarted = ({ onNextStep }: IGettingStartedProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { searchParams, setSearchParams, clearParams } = useSetSearchParam();
  const { isAssetManager, isManager } = useGetUserRole();
  const { setInfoModal, defaultModalInfo } = useIdentificationFormContext();

  const { loading: identificationLoading, isReidentification } =
    useSelector(selectIdentification) || {};
  const { loading: diiaLoading } = useSelector(selectDiia) || {};
  const { isGettingStartedCompleted } = useSelector(selectCompletedForms) || {};
  const prevGettingStarted = useSelector(selectGettingStartedBack) || {};

  const [current, setCurrent] = useState(Number(isGettingStartedCompleted));
  const [isExistingApplication, setIsExistingApplication] = useState(false);
  const [isVerificationModalOpened, setIsVerificationModalOpened] = useState(false);
  const fetchStarted = useRef(false);

  useEffect(() => {
    const formId = searchParams.get('formId');
    if (
      Number(isGettingStartedCompleted) &&
      prevGettingStarted.applicationId &&
      (formId === IdentificationFormSteps.gettingStarted.toString() || !formId)
    ) {
      setCurrent(Number(isGettingStartedCompleted));
      setIsExistingApplication(false);
    }
  }, [isGettingStartedCompleted, prevGettingStarted.applicationId]);

  const form = useGettingStartedForm(isExistingApplication, current);
  const { isDirty } = form.formState;
  const [identificationNumber, verification, applicationId, clientType] = useWatch({
    control: form.control,
    name: ['identificationNumber', 'verification', 'applicationId', 'type'],
  });

  useLayoutEffect(() => {
    const applicationNumber = searchParams.get('applicationNumber');
    const paramIdentificationNumber = searchParams.get('identificationNumber');
    if (applicationNumber && paramIdentificationNumber && !applicationId && !fetchStarted.current) {
      handleExistingApplication(applicationNumber, paramIdentificationNumber, true);
    }
  }, []);

  const isLoading = identificationLoading || diiaLoading;
  const identificationType = isReidentification
    ? IdentificationType.RE_IDENTIFICATION
    : IdentificationType.PRIMARY;

  const isVerificationOpened = isVerificationModalOpened || !!isAssetManager;
  const changeVideoVerification =
    verification === VerificationChoice.VIDEO &&
    !(isExistingApplication && current) &&
    !!applicationId;

  const handleInfoModalClose = useCallback((callback?: () => void) => {
    setInfoModal(defaultModalInfo);
    callback?.();
  }, []);

  const setError = (text: string | ReactNode) => {
    dispatch(
      setIdentificationError({
        errorMessage: text,
        discard: () => navigate('/services'),
      })
    );
  };

  const handleIdentification = (isReidentification: boolean, b2CheckResult: TB2CheckResult[]) => {
    if (isReidentification) {
      if (!b2CheckResult?.[0]?.isContragent) {
        setInfoModal({
          ...defaultModalInfo,
          isOpened: true,
          message: infoMessages.OPEN_AN_ACCOUNT,
          primaryButtonTitle: InfoModalButtonTitle.OPEN_ACCOUNT,
          onClose: () => handleInfoModalClose(() => clearParams()),
          onPrimaryButtonClick: () => handleInfoModalClose(() => clearParams()),
        });
        setCurrent(-1);
      }
    } else {
      if (b2CheckResult?.[0]?.isContragent) {
        const onClose = () =>
          handleInfoModalClose(() => {
            setSearchParams('type', IdentificationType.RE_IDENTIFICATION);
            setSearchParams('identificationNumber', identificationNumber as string);
          });
        setInfoModal({
          ...defaultModalInfo,
          isOpened: true,
          message: infoMessages.UPDATE_DATA,
          primaryButtonTitle: InfoModalButtonTitle.UPDATE_DATA,
          onClose,
          onPrimaryButtonClick: onClose,
        });
        setCurrent(-1);
      }
    }
  };

  const handleReIdentificationFopStatus = (data: TApplicationStatus, isSoleTrader: boolean) => {
    if (
      [InfoStatusContragentCodes.NEW, InfoStatusContragentCodes.UNDER_DEVELOPMENT].includes(
        data?.status
      )
    ) {
      setIsExistingApplication(true);
    } else {
      if (isSoleTrader && isReidentification) {
        setInfoModal({
          ...defaultModalInfo,
          isOpened: true,
          message: infoMessages.DOCUMENTS_SIGNED_AND_SENT,
          primaryButtonTitle: InfoModalButtonTitle.LOG_OUT,
          onClose: () => handleInfoModalClose(() => navigate('/services')),
          onPrimaryButtonClick: () => handleInfoModalClose(() => navigate('/services')),
        });
        setCurrent(-1);
        setIsExistingApplication(true);
      } else {
        setInfoModal({
          ...defaultModalInfo,
          isOpened: true,
          message: infoMessages.DOCUMENTS_SIGNED_AND_SENT,
          primaryButtonTitle: InfoModalButtonTitle.LOG_OUT,
          onClose: () => handleInfoModalClose(() => navigate('/services')),
          onPrimaryButtonClick: () => handleInfoModalClose(() => navigate('/services')),
        });
        setCurrent(-1);
        setIsExistingApplication(true);
      }
    }
  };

  const handleOtherStatuses = (data: TApplicationStatus, isSoleTrader: boolean) => {
    if (isSoleTrader && isReidentification) {
      handleReIdentificationFopStatus(data, isSoleTrader);
    } else if (
      [InfoStatusContragentCodes.NEW, InfoStatusContragentCodes.UNDER_DEVELOPMENT].includes(
        data?.status
      )
    ) {
      setIsExistingApplication(true);
    } else if (
      [
        InfoStatusContragentCodes.READY_TO_SHIP,
        InfoStatusContragentCodes.IN_PROCESSING_BY_THE_BANK,
      ].includes(data?.status)
    ) {
      if (isSoleTrader && isReidentification) {
        setInfoModal({
          ...defaultModalInfo,
          isOpened: true,
          message: infoMessages.DOCUMENTS_SIGNED_AND_SENT,
          primaryButtonTitle: InfoModalButtonTitle.LOG_OUT,
          onClose: () => handleInfoModalClose(() => navigate('/services')),
          onPrimaryButtonClick: () => handleInfoModalClose(() => navigate('/services')),
        });
        setCurrent(-1);
        setIsExistingApplication(true);
      } else {
        setInfoModal({
          ...defaultModalInfo,
          isOpened: true,
          message: infoMessages.DOCUMENTS_SIGNED_AND_SENT,
          primaryButtonTitle: InfoModalButtonTitle.LOG_OUT,
          onClose: () => handleInfoModalClose(() => navigate('/services')),
          onPrimaryButtonClick: () => handleInfoModalClose(() => navigate('/services')),
        });
        setCurrent(-1);
        setIsExistingApplication(true);
      }
    } else if (data?.status === InfoStatusContragentCodes.AGREED) {
      setInfoModal({
        ...defaultModalInfo,
        isOpened: true,
        message: infoMessages.APPLICATION_APPROVED,
        primaryButtonTitle: InfoModalButtonTitle.LOG_OUT,
        onClose: () => handleInfoModalClose(() => navigate('/services')),
        onPrimaryButtonClick: () => handleInfoModalClose(() => navigate('/services')),
      });
      setCurrent(-1);
      setIsExistingApplication(true);
    } else if (data?.status === InfoStatusContragentCodes.REJECTED) {
      setError(infoMessages.APPLICATION_REJECTED);
      setIsExistingApplication(true);
    } else if (data?.status === InfoStatusContragentCodes.CLOSED) {
      setError(infoMessages.APPLICATION_CLOSED);
      setIsExistingApplication(true);
    }
  };

  const checkClient = async (clientType: ClientTypes) => {
    try {
      const data = await fetchApplicationStatus(identificationNumber || '');

      if (data?.status === InfoStatusContragentCodes.JUST_REGISTERED) {
        const b2CheckResult = await fetchB2CheckResult(identificationNumber || '');
        handleIdentification(isReidentification, b2CheckResult);
      } else {
        handleOtherStatuses(data, clientType === ClientTypes.SOLE_TRADER);
      }
    } catch {
      setError(errorMessages.SERVICE_UNAVAILABLE);
    }
  };

  const handleSetContragrntType = async () => {
    try {
      const currentContragentType = getContragentType(identificationNumber as string);
      const contragent = contragentTypes.find((type) => type.value === currentContragentType);
      if (contragent) {
        const clientType = Object.keys(CLIENT_TYPES_ID).find(
          (item) => CLIENT_TYPES_ID[item as ClientTypes] === contragent.value
        ) as ClientTypes;

        dispatch(
          setGettingStarted({
            contragentType: contragent,
            type: clientType,
            identificationType,
            identificationNumber,
          } as TGettingStarted)
        );
        form.setValue('contragentType', contragent);
        form.setValue('type', clientType);
        form.setValue('identificationType', identificationType);

        await checkClient(clientType);
        setSearchParams('identificationNumber', identificationNumber || '');
        setCurrent((step) => step + 1);
      }
    } catch {
      setError(errorMessages.SERVICE_UNAVAILABLE);
    }
  };

  const handleGoBack = () => {
    const identificationNumber = form.getValues('identificationNumber');
    form.reset();
    form.setValue('identificationNumber', identificationNumber, {
      shouldDirty: false,
      shouldTouch: false,
    });
    setIsExistingApplication(false);
    setCurrent((step) => step - 1);
  };

  const handleExistingApplication = async (
    applicationNumber: string,
    identificationNumber: string,
    isRedirect?: boolean
  ) => {
    fetchStarted.current = true;
    try {
      await api.get(
        `/identification/application/${applicationNumber}?code=${identificationNumber}`
      );
      form.clearErrors();
    } catch (error) {
      if (isRedirect) {
        const responseMessage = ((error as AxiosError).response?.data as TErrorResponse)?.message;
        setError(responseMessage || errorMessages.SERVICE_UNAVAILABLE);
      } else {
        form.setError('applicationNumber', {
          message: errorMessages.INVALID_APPLICATION_NUMBER,
        });
      }
      return;
    }

    const { gettingStarted } = await dispatch(
      fetchIdentificationForm({ applicationId: applicationNumber as string })
    ).unwrap();

    const params = {
      gettingStarted: gettingStarted as TGettingStarted,
      applicationNumber,
      isRedirect,
    };
    const isReidentification = searchParams.get('type') === IdentificationType.RE_IDENTIFICATION;

    if (
      (gettingStarted as TGettingStarted).identificationType === IdentificationType.PRIMARY &&
      isReidentification
    ) {
      setSearchParams('type', IdentificationType.PRIMARY);
      setSearchParams('identificationNumber', identificationNumber);
      dispatch(setIsReidentification(false));
      setInfoModal({
        ...defaultModalInfo,
        isOpened: true,
        title: 'Незавершена заявка на відкриття рахунку',
        message: `Для встановлення ділових відносин з подальшим відкриттям рахунку завершіть заповнення анкети №${identificationNumber}`,
        primaryButtonTitle: 'Зрозуміло',
        onClose: () => handleInfoModalClose(() => handleContinue(params)),
        onPrimaryButtonClick: () => handleInfoModalClose(() => handleContinue(params)),
      });
    } else if (
      (gettingStarted as TGettingStarted).identificationType ===
        IdentificationType.RE_IDENTIFICATION &&
      !isReidentification
    ) {
      setSearchParams('type', IdentificationType.RE_IDENTIFICATION);
      setInfoModal({
        ...defaultModalInfo,
        isOpened: true,
        title: 'Незавершена заявка на оновлення даних',
        message: `Для оновлення даних завершіть заповнення анкети №${identificationNumber}`,
        primaryButtonTitle: 'Зрозуміло',
        onClose: () => handleInfoModalClose(() => handleContinue(params)),
        onPrimaryButtonClick: () => handleInfoModalClose(() => handleContinue(params)),
      });
    } else {
      await handleContinue(params);
    }
  };

  const handleCloseModal = () => {
    setIsVerificationModalOpened(false);
  };

  const handleContinue = async ({ gettingStarted, applicationNumber }: IContinueProps) => {
    try {
      dispatch(
        updateFormData({
          field: IdentificationFormStepNames.gettingStarted,
          data: cloneDeep(gettingStarted),
        })
      );
      setSearchParams('applicationNumber', applicationNumber);
    } catch (error) {
      console.log(error);
    }
  };

  const handleNextStep =
    (isDirty: boolean, disableGoNext?: boolean) => async (values: TGettingStarted) => {
      const applicationNumber = form.getValues('applicationNumber');
      const applicationId = form.getValues('applicationId');
      try {
        if (current) {
          if (isGettingStartedCompleted) {
            if (isReidentification) {
              !disableGoNext && onNextStep();
              return;
            }

            if (isDirty) {
              const data = {
                gettingStarted: Object.fromEntries(
                  Object.entries(values).filter(([key]) => !QUESTION_FIELDS.includes(key))
                ),
                applicationId: applicationId!,
              };
              await dispatch(
                updateForm({ data, reportId: IdentificationFormSteps.gettingStarted })
              );
              const formData = {
                field: IdentificationFormStepNames.gettingStarted as keyof TIdentificationForm,
                data: cloneDeep(values),
              };
              dispatch(updateFormData(formData));
              dispatch(updateFormDataBack(formData));
            }
            !disableGoNext && onNextStep();
            form.reset();
            return;
          }
          if (isExistingApplication) {
            handleExistingApplication(applicationNumber || '', identificationNumber as string);
          } else {
            if (isManager) {
              const { id: applicationId, appNumber: applicationNumber } = await dispatch(
                managerCreateApplication({ identifyCode: identificationNumber as string })
              ).unwrap();
              handleVerificationSuccess(VerificationChoice.VIDEO, applicationId, applicationNumber);
            } else {
              setIsVerificationModalOpened(true);
            }
          }
        } else {
          const contragent = getContragentType(identificationNumber ?? '');
          if (contragent === CLIENT_TYPES_ID[ClientTypes.LEGAL_NON_RESIDENT]) {
            setError(errorMessages.LEGAL_FUNCTIONALITY_IN_PROGRESS_RESIDENT);
          } else {
            await handleSetContragrntType();
          }
        }
      } catch {
        setError(errorMessages.SERVICE_UNAVAILABLE);
      }
    };

  const handleVerificationSuccess = async (
    verificationType: VerificationChoice,
    applicationId: number,
    applicationNumber: string
  ) => {
    form.setValue('verification', verificationType);
    if (changeVideoVerification) {
      await dispatch(updateFormChangeVideoVerification({ applicationId }));
      onNextStep();
    } else {
      try {
        const data = { ...(form.getValues() as TGettingStarted), applicationId };
        handleCloseModal();
        await dispatch(fetchCreatedIdentificationForm({ data, applicationId })).unwrap();
        setSearchParams('applicationNumber', applicationNumber);
        onNextStep();
      } catch (error: unknown) {
        if ((error as { errorCode: number })?.errorCode === ERROR_CODES.MANAGER_NOT_FOUND) {
          setError(getManagerNotFoundMessage());
        }
        setError((error as { message: string })?.message || errorMessages.SERVICE_UNAVAILABLE);
      }
    }
  };

  return (
    <FormProvider {...form}>
      <Box component="form" onSubmit={form.handleSubmit(handleNextStep(isDirty), onInvalidForm)}>
        {!isAssetManager && (
          <Box sx={{ ...commonStyles.centerFlexContainer, flexDirection: 'column', width: '100%' }}>
            <Box
              sx={{
                width: { md: '500px', sm: '100%', xs: '100%' },
                display: !current ? 'block' : 'none',
              }}
            >
              <FormInput
                name="identificationNumber"
                label="РНОКПП або ЄДРПОУ"
                placeholder="Введіть РНОКПП/ЄДРПОУ"
                fullWidth
              />
            </Box>
            <Box
              sx={{
                width: { md: '500px', sm: '100%', xs: '100%' },
                display: isExistingApplication && current ? 'block' : 'none',
              }}
            >
              <FormInput
                name="applicationNumber"
                label={
                  <Box
                    sx={{
                      ...commonStyles.centerFlexContainer,
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2">№ заявки</Typography>
                    <InfoTooltip title={infoMessages.APPLICATION_NUMBER_SENT} />
                  </Box>
                }
                placeholder="Введіть № заявки"
                fullWidth
              />
            </Box>
            <Box
              sx={{ display: !isExistingApplication && current ? 'block' : 'none', width: '100%' }}
            >
              {isReidentification ? <CreationClientInfoReIdentification /> : <CreationClientInfo />}
            </Box>
            <Grid
              container
              spacing={2}
              sx={{ width: { xs: '100%', sm: '100%', md: '300px' }, justifyContent: 'center' }}
            >
              {!!current && !isGettingStartedCompleted && (
                <Grid size={6}>
                  <Button
                    variant="contained"
                    sx={{ minWidth: '100%' }}
                    onClick={handleGoBack}
                    disabled={isGettingStartedCompleted}
                    data-testid="back_button"
                  >
                    Назад
                  </Button>
                </Grid>
              )}
              <Grid size={6} sx={styles.buttonGrid}>
                {changeVideoVerification && !isManager && (
                  <Button
                    variant="outlined"
                    sx={{ minWidth: '100%' }}
                    type="button"
                    onClick={() => setIsVerificationModalOpened(true)}
                    data-testid="change-video-verification-button"
                  >
                    <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                      Верифікуватись повторно через Дію
                    </Box>
                    <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                      Верифікуватись повторно{'\u00A0'}
                      <br />
                      через Дію
                    </Box>
                  </Button>
                )}
                <Button
                  variant="contained"
                  sx={{ minWidth: '100%' }}
                  type="submit"
                  data-testid="getting-started-submit"
                >
                  Продовжити
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
        <SetDirtyModalValues
          saveValues={form.handleSubmit(handleNextStep(isDirty, true), onInvalidForm)}
        />
        <VerificationBlock
          setError={setError}
          handleCloseModal={handleCloseModal}
          isVerificationModalOpened={isVerificationOpened}
          onNextStep={onNextStep}
          handleVerificationSuccess={handleVerificationSuccess}
          isVideoVerification={!changeVideoVerification}
          changeVideoVerification={changeVideoVerification}
          clientType={clientType}
        />
      </Box>
      {isLoading && <FullPageLoader />}
    </FormProvider>
  );
};

export default GettingStarted;
